import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { db } from "../../kern/supabase";

/** Neuladen alle 15 Sekunden: Mehrere im Vorstand sehen in der Sitzung denselben Stand (ADR-007, kein Realtime). */
const TAKT = 15_000;

export type SitzungStatus = "vorbereitet" | "laeuft" | "abgeschlossen";

export interface Sitzungstermin {
  id: string;
  datum: string;
  uhrzeit: string | null;
  ort: string;
  status: "geplant" | "verschoben" | "abgesagt" | "abgeschlossen";
}

/** Vorstandssitzungen aus app_termine (Kern, nur gelesen) */
export function useSitzungstermine() {
  return useQuery({
    queryKey: ["termine", "sitzungen"],
    refetchInterval: TAKT,
    queryFn: async (): Promise<Sitzungstermin[]> => {
      const { data, error } = await db().from("app_termine").select("id, datum, uhrzeit, ort, status").eq("art", "vorstandssitzung").order("datum");
      if (error) throw error;
      return data as Sitzungstermin[];
    },
  });
}

/** Alle Sitzungen kurz (RLS: nur mit sitzung.leiten), um die laufende oder vorbereitete zu finden */
export function useSitzungen() {
  return useQuery({
    queryKey: ["sitzung", "alle"],
    refetchInterval: TAKT,
    queryFn: async () => {
      const { data, error } = await db().from("sitzung_sitzungen").select("id, termin_id, status");
      if (error) throw error;
      return data as { id: string; termin_id: string; status: SitzungStatus }[];
    },
  });
}

export interface Beschluss {
  id: string;
  nr: number;
  text: string;
  ergebnis: "angenommen" | "abgelehnt" | "vertagt";
  person: string | null;
  aufgabe_id: string | null;
}

export function useSitzung(terminId: string | undefined) {
  return useQuery({
    queryKey: ["sitzung", terminId],
    enabled: !!terminId,
    refetchInterval: TAKT,
    queryFn: async () => {
      const s = await db().from("sitzung_sitzungen").select("id, termin_id, status, gaeste, notizen").eq("termin_id", terminId ?? "").maybeSingle();
      if (s.error) throw s.error;
      if (!s.data) return null;
      const [a, t, b] = await Promise.all([
        db().from("sitzung_anwesende").select("person").eq("sitzung_id", s.data.id),
        db().from("sitzung_tagesordnung").select("id, nr, text, besprochen").eq("sitzung_id", s.data.id).order("nr"),
        db().from("sitzung_beschluesse").select("id, nr, text, ergebnis, person, aufgabe_id").eq("sitzung_id", s.data.id).order("nr"),
      ]);
      if (a.error) throw a.error;
      if (t.error) throw t.error;
      if (b.error) throw b.error;
      return {
        ...s.data,
        status: s.data.status as SitzungStatus,
        anwesende: a.data.map((x) => x.person),
        tagesordnung: t.data,
        beschluesse: b.data as Beschluss[],
      };
    },
  });
}

/** Sitzung zu einem Protokoll, für die gegliederte Ansicht (BAUPLAN-ABSCHLUSS.md, Abschnitt 4.3). Ohne Recht null. */
export function useProtokollSitzung(sitzungId: string | null | undefined) {
  return useQuery({
    queryKey: ["sitzung", "protokoll", sitzungId],
    enabled: !!sitzungId,
    queryFn: async () => {
      const s = await db().from("sitzung_sitzungen").select("id, termin_id, status, gaeste, notizen").eq("id", sitzungId ?? "").maybeSingle();
      if (s.error || !s.data) return null;
      const [a, t, b] = await Promise.all([
        db().from("sitzung_anwesende").select("person").eq("sitzung_id", s.data.id),
        db().from("sitzung_tagesordnung").select("id, nr, text, besprochen").eq("sitzung_id", s.data.id).order("nr"),
        db().from("sitzung_beschluesse").select("id, nr, text, ergebnis, person, aufgabe_id").eq("sitzung_id", s.data.id).order("nr"),
      ]);
      if (a.error || t.error || b.error) return null;
      return { ...s.data, anwesende: a.data.map((x) => x.person), tagesordnung: t.data, beschluesse: b.data as Beschluss[] };
    },
  });
}

/** „n anwesend · n Beschlüsse“ je Sitzung für die Liste der Protokolle. Ohne Recht eine leere Liste. */
export function useSitzungZahlen() {
  return useQuery({
    queryKey: ["sitzung", "zahlen"],
    staleTime: 30_000,
    queryFn: async () => {
      const [a, b] = await Promise.all([db().from("sitzung_anwesende").select("sitzung_id"), db().from("sitzung_beschluesse").select("sitzung_id")]);
      const zahlen = new Map<string, { anwesend: number; beschluesse: number }>();
      const zu = (id: string) => {
        const z = zahlen.get(id) ?? { anwesend: 0, beschluesse: 0 };
        zahlen.set(id, z);
        return z;
      };
      for (const x of a.error ? [] : a.data) zu(x.sitzung_id).anwesend++;
      for (const x of b.error ? [] : b.data) zu(x.sitzung_id).beschluesse++;
      return zahlen;
    },
  });
}

/** Laufender Sprint aus der Datenbank (Modul aufgaben, nur gelesen, kein Import aus dem Modul) */
export function useLaufenderSprint() {
  return useQuery({
    queryKey: ["sprints", "laufend"],
    refetchInterval: TAKT,
    queryFn: async () => {
      const { data, error } = await db().from("aufgaben_sprints").select("id, name, ende").eq("status", "laufend").maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

/** Aufgaben aus Beschlüssen: Titel und Ort (über die View aufgaben_liste, RLS) */
export function useBeschlussAufgaben(ids: readonly string[]) {
  return useQuery({
    queryKey: ["aufgaben", "beschluesse", [...ids].sort().join(",")],
    enabled: ids.length > 0,
    refetchInterval: TAKT,
    queryFn: async () => {
      // "*": vor der Migration Stufe 2 gibt es die Spalte personen noch nicht.
      const { data, error } = await db().from("aufgaben_liste").select("*").in("id", [...ids]);
      if (error) throw error;
      return data.map((a) => {
        const z = a as Record<string, unknown>;
        const personen = Array.isArray(z.personen) ? (z.personen as unknown[]).filter((x): x is string => typeof x === "string") : a.person ? [a.person] : [];
        return { id: a.id, titel: a.titel, sprint_name: a.sprint_name, sprint_status: a.sprint_status, personen };
      });
    },
  });
}

export interface VorhabenVorschlag {
  id: string;
  titel: string;
  frist_datum: string | null;
  vorhaben_id: string | null;
  vorhaben_name: string | null;
  personen: string[];
  eltern_id: string | null;
  sprint_id: string | null;
  status: string;
  archiviert_am: string | null;
}

/** „Aus Vorhaben“ (BAUPLAN-PAKETE.md, Abschnitt 9.6): Aufgaben mit Vorhaben, ohne Sprint, offen, Frist bis zur Grenze. Über die View
 *  aufgaben_liste (RLS), Namen der Vorhaben aus aufgaben_vorhaben. Modul aufgaben nur gelesen, kein Import. */
export function useVorhabenVorschlaege(grenze: string) {
  return useQuery({
    queryKey: ["aufgaben", "aus-vorhaben", grenze],
    refetchInterval: TAKT,
    queryFn: async (): Promise<VorhabenVorschlag[]> => {
      const [a, v] = await Promise.all([
        // "*": vor der Migration Stufe 2 gibt es die Spalte personen noch nicht
        db()
          .from("aufgaben_liste")
          .select("*")
          .not("vorhaben_id", "is", null)
          .is("sprint_id", null)
          .is("eltern_id", null)
          .is("archiviert_am", null)
          .neq("status", "erledigt")
          .lte("frist_datum", grenze)
          .order("frist_datum"),
        db().from("aufgaben_vorhaben").select("id, name"),
      ]);
      if (a.error) throw a.error;
      if (v.error) throw v.error;
      const namen = new Map(v.data.map((x) => [x.id, x.name]));
      return a.data.map((x) => {
        const z = x as Record<string, unknown>;
        const personen = Array.isArray(z.personen) ? (z.personen as unknown[]).filter((p): p is string => typeof p === "string") : x.person ? [x.person] : [];
        return {
          id: String(x.id),
          titel: String(x.titel ?? ""),
          frist_datum: x.frist_datum,
          vorhaben_id: x.vorhaben_id,
          vorhaben_name: x.vorhaben_id ? (namen.get(x.vorhaben_id) ?? null) : null,
          personen,
          eltern_id: x.eltern_id,
          sprint_id: x.sprint_id,
          status: String(x.status ?? ""),
          archiviert_am: x.archiviert_am,
        };
      });
    },
  });
}

/** „In den Sprint“ aus der Sitzung: dieselbe RPC wie im Modul aufgaben (aufgaben_in_sprint, nur mit sprint.planen) */
export function useInSprint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db().rpc("aufgaben_in_sprint", { p_id: id });
      if (error) throw error;
    },
    onSettled: () => {
      for (const k of ["aufgaben", "aufgabe", "vorhaben", "mitteilungen"]) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
}

export function useSitzungAktionen() {
  const qc = useQueryClient();
  const neu = () => {
    for (const k of ["sitzung", "aufgaben", "aufgabe", "termine", "protokolle", "mitteilungen"]) void qc.invalidateQueries({ queryKey: [k] });
  };
  const rpc = async <T,>(fn: PromiseLike<{ data: T; error: unknown }>): Promise<T> => {
    const { data, error } = await fn;
    if (error) throw error;
    return data;
  };
  return {
    vorbereiten: useMutation({ mutationFn: (termin: string) => rpc(db().rpc("sitzung_vorbereiten", { p_termin: termin })), onSettled: neu }),
    starten: useMutation({ mutationFn: (termin: string) => rpc(db().rpc("sitzung_starten", { p_termin: termin })), onSettled: neu }),
    anwesenheit: useMutation({
      mutationFn: ({ sitzung, person, anwesend }: { sitzung: string; person: string; anwesend: boolean }) =>
        rpc(db().rpc("sitzung_anwesenheit_setzen", { p_sitzung: sitzung, p_person: person, p_anwesend: anwesend })),
      onSettled: neu,
    }),
    bearbeiten: useMutation({
      mutationFn: ({ sitzung, gaeste, notizen }: { sitzung: string; gaeste?: string; notizen?: string }) =>
        rpc(db().rpc("sitzung_bearbeiten", { p_sitzung: sitzung, ...(gaeste !== undefined ? { p_gaeste: gaeste } : {}), ...(notizen !== undefined ? { p_notizen: notizen } : {}) })),
      onSettled: neu,
    }),
    topAnlegen: useMutation({ mutationFn: ({ sitzung, text }: { sitzung: string; text: string }) => rpc(db().rpc("sitzung_top_anlegen", { p_sitzung: sitzung, p_text: text })), onSettled: neu }),
    topBesprochen: useMutation({
      mutationFn: ({ top, besprochen }: { top: string; besprochen: boolean }) => rpc(db().rpc("sitzung_top_besprochen", { p_top: top, p_besprochen: besprochen })),
      onSettled: neu,
    }),
    topEntfernen: useMutation({ mutationFn: (top: string) => rpc(db().rpc("sitzung_top_entfernen", { p_top: top })), onSettled: neu }),
    beschluss: useMutation({
      mutationFn: (b: { sitzung: string; text: string; ergebnis: Beschluss["ergebnis"]; aufgabe: "keine" | "backlog" | "sprint"; person: string | null }) =>
        rpc(db().rpc("sitzung_beschluss_fassen", { p_sitzung: b.sitzung, p_text: b.text, p_ergebnis: b.ergebnis, p_aufgabe: b.aufgabe, ...(b.person ? { p_person: b.person } : {}) })),
      onSettled: neu,
    }),
    abschliessen: useMutation({ mutationFn: (sitzung: string) => rpc(db().rpc("sitzung_abschliessen", { p_sitzung: sitzung })), onSettled: neu }),
  };
}

export interface Protokoll {
  id: string;
  art: "vorstand" | "mitgliederversammlung";
  datum: string;
  titel: string;
  text: string;
  drive_url: string | null;
  freigegeben: boolean;
  sitzung_id: string | null;
}

export function useProtokolle() {
  return useQuery({
    queryKey: ["protokolle"],
    refetchInterval: TAKT,
    queryFn: async (): Promise<Protokoll[]> => {
      const { data, error } = await db().from("sitzung_protokolle").select("id, art, datum, titel, text, drive_url, freigegeben, sitzung_id").order("datum", { ascending: false });
      if (error) throw error;
      return data as Protokoll[];
    },
  });
}

export function useProtokollAktionen() {
  const qc = useQueryClient();
  const neu = () => void qc.invalidateQueries({ queryKey: ["protokolle"] });
  const rpc = async <T,>(fn: PromiseLike<{ data: T; error: unknown }>): Promise<T> => {
    const { data, error } = await fn;
    if (error) throw error;
    return data;
  };
  return {
    anlegen: useMutation({
      mutationFn: (p: { art: "vorstand" | "mitgliederversammlung"; datum: string; titel: string; drive_url: string }) =>
        rpc(db().rpc("sitzung_protokoll_anlegen", { p_art: p.art, p_datum: p.datum, p_titel: p.titel, p_drive_url: p.drive_url })),
      onSettled: neu,
    }),
    bearbeiten: useMutation({
      mutationFn: (p: { id: string; text: string }) => rpc(db().rpc("sitzung_protokoll_bearbeiten", { p_id: p.id, p_text: p.text })),
      onSettled: neu,
    }),
    freigeben: useMutation({
      mutationFn: (p: { id: string; frei: boolean }) => rpc(db().rpc("sitzung_protokoll_freigeben", { p_id: p.id, p_frei: p.frei })),
      onSettled: neu,
    }),
  };
}

export function useAblage() {
  return useQuery({
    queryKey: ["ablage"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const [r, f] = await Promise.all([
        db().from("sitzung_ablage").select("register, name, beschreibung, drive_url").order("register"),
        db().from("sitzung_ablage_fehlt").select("id, register, text, erledigt, aufgabe_id").order("erstellt_am"),
      ]);
      if (r.error) throw r.error;
      if (f.error) throw f.error;
      return { register: r.data, fehlt: f.data };
    },
  });
}

export function useAblageAktionen() {
  const qc = useQueryClient();
  const neu = () => {
    void qc.invalidateQueries({ queryKey: ["ablage"] });
    void qc.invalidateQueries({ queryKey: ["aufgaben"] });
  };
  const rpc = async <T,>(fn: PromiseLike<{ data: T; error: unknown }>): Promise<T> => {
    const { data, error } = await fn;
    if (error) throw error;
    return data;
  };
  return {
    setzen: useMutation({
      mutationFn: (p: { register: string; drive_url: string }) => rpc(db().rpc("sitzung_ablage_setzen", { p_register: p.register, p_drive_url: p.drive_url })),
      onSettled: neu,
    }),
    fehltAnlegen: useMutation({ mutationFn: (p: { register: string; text: string }) => rpc(db().rpc("sitzung_fehlt_anlegen", { p_register: p.register, p_text: p.text })), onSettled: neu }),
    fehltErledigt: useMutation({ mutationFn: (p: { id: string; erledigt: boolean }) => rpc(db().rpc("sitzung_fehlt_erledigt", { p_id: p.id, p_erledigt: p.erledigt })), onSettled: neu }),
    alsAufgabe: useMutation({ mutationFn: (id: string) => rpc(db().rpc("sitzung_fehlt_als_aufgabe", { p_id: id })), onSettled: neu }),
  };
}
